/**
 * Maritime seamark overlay for Mapbox GL.
 *
 * NOAA's public XYZ tile service was shut down in Oct 2021 and their remaining
 * ArcGIS endpoints either return empty tiles for coastal areas or require
 * self-hosting MBTiles. We fall back to OpenSeaMap's seamark overlay — an
 * OpenStreetMap-based XYZ service (no key needed) that renders every IALA
 * navigation aid, buoy, light, harbor limit, and anchorage on top of the
 * base map. Globally covered.
 *
 * License: OpenStreetMap ODbL. Attribution shown on the map.
 */

export const NOAA_SOURCE_ID = "seamarks-src";
export const NOAA_LAYER_ID = "seamarks-layer";
export const NOAA_ATTRIBUTION =
    '© <a href="https://www.openseamap.org/" target="_blank" rel="noreferrer">OpenSeaMap</a> · © OSM contributors';
const SEAMARK_TILE_URL = "https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png";

/** Add the source + layer (no-op if already present). Call after `map.on('load')`. */
export function addNoaaLayer(map, { visible = true, opacity = 0.85, beforeLayerId } = {}) {
    if (!map || map._removed) return;
    if (!map.getSource(NOAA_SOURCE_ID)) {
        map.addSource(NOAA_SOURCE_ID, {
            type: "raster",
            tiles: [SEAMARK_TILE_URL],
            tileSize: 256,
            attribution: NOAA_ATTRIBUTION,
            minzoom: 0,
            maxzoom: 18,
        });
    }
    if (!map.getLayer(NOAA_LAYER_ID)) {
        map.addLayer(
            {
                id: NOAA_LAYER_ID,
                type: "raster",
                source: NOAA_SOURCE_ID,
                layout: { visibility: visible ? "visible" : "none" },
                paint: { "raster-opacity": opacity },
            },
            beforeLayerId, // insert BEFORE labels so orange pins + labels stay on top
        );
    }
}

/** Show or hide the chart without touching the source. */
export function setNoaaVisibility(map, visible) {
    if (!map || map._removed) return;
    if (!map.getLayer(NOAA_LAYER_ID)) return;
    map.setLayoutProperty(NOAA_LAYER_ID, "visibility", visible ? "visible" : "none");
}

/** Change opacity while keeping the layer live. */
export function setNoaaOpacity(map, opacity) {
    if (!map || map._removed) return;
    if (!map.getLayer(NOAA_LAYER_ID)) return;
    map.setPaintProperty(NOAA_LAYER_ID, "raster-opacity", opacity);
}

/**
 * Find the first symbol/label layer so we can insert the NOAA raster BEFORE it —
 * this keeps Mapbox's place names, road labels, and our pin labels on top.
 */
export function firstSymbolLayerId(map) {
    if (!map || map._removed) return undefined;
    const layers = map.getStyle()?.layers || [];
    const symbol = layers.find((l) => l.type === "symbol");
    return symbol?.id;
}
